import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { Edit02Icon, FloppyDiskIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import SaveNotesDialog from './SaveNotesDialog'

/**
 * ItemNotesModal - Modal for editing order item notes
 *
 * IMPORTANT: This component uses local state for draft notes. To reset state
 * when the orderItem changes, use the key prop in the parent component:
 *
 * @example
 * <ItemNotesModal
 *   key={orderItem?.id}  // Resets all state when item changes
 *   orderItem={orderItem}
 *   // ... other props
 * />
 */
const ItemNotesModal = ({ handleUpdate, onClose, open, orderItem }) => {
    const { t } = useTranslation()

    const [isEditingCustomerNotes, setIsEditingCustomerNotes] = useState(false)
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

    // Local draft state - initialized from prop, updated via key prop pattern
    const [draftCustomerNotes, setDraftCustomerNotes] = useState(orderItem?.customerNotes || '')

    const itemVendorNotes = orderItem?.vendorNotes
    const name = orderItem?.name

    const toggleEditVendorNotes = () => {
        if (isEditingCustomerNotes) {
            // Save draft to parent when toggling edit mode off
            handleUpdate({ ...orderItem, customerNotes: draftCustomerNotes })
        }
        setIsEditingCustomerNotes((v) => !v)
    }

    const handleCustomerNotesChange = (event) => {
        const { value } = event.target
        // Update local draft state only - don't persist to parent yet
        setDraftCustomerNotes(value)
    }

    const handleClose = (_, reason) => {
        if (reason !== 'backdropClick') {
            if (isEditingCustomerNotes) {
                setShowConfirmationDialog(true)
            } else {
                onClose()
            }
        }
    }

    const onSaveNotesDialogCancel = () => {
        setShowConfirmationDialog(false)
        onClose()
    }

    const onSaveNotesDialogConfirm = () => {
        // Save draft to parent when user confirms
        handleUpdate({ ...orderItem, customerNotes: draftCustomerNotes })
        setShowConfirmationDialog(false)
        onClose()
    }

    return (
        <Dialog
            open={open}
            PaperProps={{ sx: { width: 400 } }}
            disableEscapeKeyDown
            onClose={handleClose}
        >
            <DialogTitle
                alignItems="center"
                data-testid="order-item-notes-modal"
                display="flex"
                justifyContent="space-between"
            >
                <Typography
                    sx={{
                        textTransform: 'uppercase',
                    }}
                    variant="strong1"
                >
                    {t('NOTES FOR')} {name}
                </Typography>
                <IconButton
                    color="secondary"
                    size="small"
                    onClick={handleClose}
                >
                    <HugeiconsIcon icon={Cancel01Icon} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}
                >
                    {orderItem ? (
                        <Box>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    data-testid="customer-notes-label"
                                    variant="strong2"
                                >
                                    {t('Customer notes')}
                                </Typography>
                                <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={toggleEditVendorNotes}
                                >
                                    <HugeiconsIcon
                                        altIcon={FloppyDiskIcon}
                                        icon={Edit02Icon}
                                        showAlt={isEditingCustomerNotes}
                                        size="18"
                                    />
                                </IconButton>
                            </Box>

                            <TextField
                                color="secondary"
                                data-testid="customer-notes"
                                helperText={t('Optionally add notes about this part.')}
                                minRows={3}
                                placeholder={t('No customer notes saved on this part')}
                                slotProps={{
                                    input: {
                                        readOnly: !isEditingCustomerNotes,
                                        style: { fontSize: 14 },
                                    },
                                }}
                                value={draftCustomerNotes}
                                fullWidth
                                multiline
                                onChange={handleCustomerNotesChange}
                            />
                        </Box>
                    ) : null}
                    {itemVendorNotes ? (
                        <Box>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    data-testid="vendor-notes-label"
                                    variant="strong2"
                                >
                                    {t('Vendor notes')}
                                </Typography>
                            </Box>
                            <TextField
                                color="secondary"
                                data-testid="vendor-notes"
                                helperText={t('Vendor notes about this part.')}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        style: { fontSize: 14 },
                                    },
                                }}
                                value={itemVendorNotes}
                                fullWidth
                                multiline
                            />
                        </Box>
                    ) : null}
                </Box>
            </DialogContent>
            <SaveNotesDialog
                open={showConfirmationDialog}
                onCancel={onSaveNotesDialogCancel}
                onConfirm={onSaveNotesDialogConfirm}
            />
        </Dialog>
    )
}

ItemNotesModal.propTypes = {
    handleUpdate: PropTypes.func,
    miscItem: PropTypes.object,
    open: PropTypes.bool,
    orderItem: PropTypes.object,
    onClose: PropTypes.func,
}

export default ItemNotesModal
